import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { motion } from "framer-motion";
import { getArticleBySlug } from "@/lib/articles";
import { ArrowLeft, Clock, Calendar, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import Seo from "@/components/Seo";
import { SITE_URL, SITE_LOGO } from "@/lib/seoConfig";

// `slug` is passed as a prop for statically pre-rendered /resources/<slug>
// routes. When rendered at /ResourcesArticle?slug=... it falls back to the
// query string (client-side only).
export default function ResourcesArticle({ slug: slugProp }) {
  const slug =
    slugProp ||
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("slug")
      : null);

  const article = getArticleBySlug(slug);

  const handleShare = () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({
        title: article.title,
        text: article.summary,
        url: window.location.href,
      });
    } else if (typeof navigator !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard");
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen pt-32 px-6 lg:px-8">
        <Seo title="Article not found | Vyro Resources" description="The requested article could not be found." />
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article not found</h1>
          <Link to={createPageUrl("Resources")}>
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${SITE_URL}/resources/${article.slug}`;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: article.title,
        description: article.seo_description || article.summary,
        image: article.image_url ? [article.image_url] : undefined,
        datePublished: article.published_date || undefined,
        dateModified: article.published_date || undefined,
        author: { "@type": "Organization", name: "Vyro", url: SITE_URL },
        publisher: {
          "@type": "Organization",
          name: "Vyro",
          url: SITE_URL,
          logo: { "@type": "ImageObject", url: SITE_LOGO },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        url: canonicalUrl,
        articleSection: article.primary_topic || undefined,
        keywords: Array.isArray(article.topics) ? article.topics.join(", ") : undefined,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/Resources` },
          { "@type": "ListItem", position: 3, name: article.title, item: canonicalUrl },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={`${article.seo_title || article.title} | Vyro Resources`}
        description={article.seo_description || article.summary}
        canonical={canonicalUrl}
        image={article.image_url}
      />
      {/* Article structured data (in-document; SSG-safe — <Head> strips <script>). */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <article className="pt-32 pb-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link
            to={createPageUrl("Resources")}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>

          {/* Header */}
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-8 pb-8 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{article.read_time_minutes} min read</span>
              </div>
              {article.published_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(article.published_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}</span>
                </div>
              )}
              {article.target_audience?.[0] && (
                <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                  {article.target_audience[0]}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="ml-auto"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            {/* Hero Image */}
            {article.image_url && (
              <div className="mb-12 -mx-6 lg:-mx-8">
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="w-full h-[400px] object-cover rounded-2xl"
                />
              </div>
            )}

            {/* Summary */}
            <p className="text-lg text-slate-600 mb-12 leading-relaxed">
              {article.summary}
            </p>
          </div>

          <style>{`
            /* Blog content body — Vyro baseline */
.bodyContent {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
    "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji";
  font-size: 1rem;
  line-height: 1.75;
  color: #0F172A;
  overflow-wrap: anywhere;
  word-break: normal;
}
.bodyContent :where(p, ul, ol, blockquote, pre, figure, table) {
  margin: 0;
  margin-top: 1.1em;
}
.bodyContent :where(h2, h3, h4, h5, h6) {
  margin: 0;
  margin-top: 1.8em;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: #0F172A;
}
.bodyContent h1 { font-size: 2rem; font-weight: 700; }
.bodyContent h2 { font-size: 1.75rem; font-weight: 700; }
.bodyContent h3 { font-size: 1.4rem; font-weight: 700; }
.bodyContent h4 { font-size: 1.15rem; font-weight: 700; }
.bodyContent h5, .bodyContent h6 { font-size: 1rem; font-weight: 700; }
.bodyContent :where(h2, h3, h4, h5, h6) + :where(p, ul, ol, blockquote, pre, table, figure) {
  margin-top: 0.8em;
}
.bodyContent a {
  color: #3B82F6;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  text-decoration-thickness: 1px;
}
.bodyContent a:hover { text-decoration-thickness: 2px; }
.bodyContent ul, .bodyContent ol { padding-left: 1.25em; }
.bodyContent li { margin-top: 0.4em; }
.bodyContent li > :where(ul, ol) { margin-top: 0.4em; }
.bodyContent strong { font-weight: 700; }
.bodyContent em { font-style: italic; }
.bodyContent code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.95em;
  background: rgba(15, 23, 42, 0.06);
  padding: 0.15em 0.35em;
  border-radius: 6px;
}
.bodyContent pre {
  margin-top: 1.1em;
  padding: 1em 1.1em;
  border-radius: 12px;
  background: #0F172A;
  color: #F8FAFC;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.bodyContent pre code { background: transparent; padding: 0; border-radius: 0; font-size: 0.95em; }
.bodyContent blockquote {
  padding: 0.75em 1em;
  border-left: 3px solid rgba(59, 130, 246, 0.8);
  background: rgba(15, 23, 42, 0.03);
  border-radius: 10px;
}
.bodyContent blockquote p { margin-top: 0.8em; }
.bodyContent blockquote p:first-child { margin-top: 0; }
.bodyContent hr { border: 0; height: 1px; margin-top: 2em; background: rgba(15, 23, 42, 0.12); }
.bodyContent table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
.bodyContent th, .bodyContent td {
  padding: 0.7em 0.8em;
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
  text-align: left;
  vertical-align: top;
}
.bodyContent th { font-weight: 700; color: #0F172A; background: rgba(15, 23, 42, 0.03); }
.bodyContent img { max-width: 100%; height: auto; border-radius: 12px; display: block; }
.bodyContent figure { margin-top: 1.3em; }
.bodyContent figcaption { margin-top: 0.6em; font-size: 0.9rem; color: rgba(15, 23, 42, 0.7); }
          `}</style>

          {/* Content */}
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bodyContent prose prose-slate prose-lg max-w-none">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>
          </motion.div>

          {/* Tags */}
          {article.topics && article.topics.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Topics</h3>
              <div className="flex flex-wrap gap-2">
                {article.topics.map(topic => (
                  <span
                    key={topic}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-full"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Ready to transform your automotive retail?
            </h3>
            <p className="text-slate-600 mb-6">
              See how Vyro can help your business scale.
            </p>
            <Link to={createPageUrl("Demo")}>
              <Button className="bg-[#3B82F6] hover:bg-[#2563EB]">
                Book a Demo
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
